/**
 * Organization Service
 *
 * API calls for organization-level operations including email branding
 */

import api from './api';

export interface BrandingSettings {
  logoUrl: string | null;
  brandColor: string | null;
  organizationName: string | null;
}

export interface BrandingResponse {
  branding: BrandingSettings;
  hasCustomColorFeature: boolean;
}

export interface UpdateBrandingRequest {
  brandColor?: string | null;
}

export interface EmailBrandingSettings {
  emailLogoUrl: string | null;
  emailHeaderHtml: string | null;
  emailFooterHtml: string | null;
  emailHeaderBgColor: string | null;
  emailLogoAlignment: 'left' | 'center' | 'right';
  emailLogoHeight: number;
  brandColor: string | null;
}

export interface EmailBrandingResponse {
  emailBranding: EmailBrandingSettings;
  hasEmailBrandingFeature: boolean;
}

export interface UpdateEmailBrandingRequest {
  emailHeaderHtml?: string | null;
  emailFooterHtml?: string | null;
  emailHeaderBgColor?: string | null;
  emailLogoAlignment?: 'left' | 'center' | 'right';
  emailLogoHeight?: number;
}

// Organization Switcher Types
export interface UserOrganization {
  id: number;
  name: string;
  role: 'owner' | 'admin' | 'member' | 'guest';
  isPrimary: boolean;
  logoUrl: string | null;
  brandColor: string | null;
}

export interface UserOrganizationsResponse {
  organizations: UserOrganization[];
  currentOrganizationId: number | null;
}

export interface SwitchOrganizationResponse {
  success: boolean;
  message: string;
  organizationId: number;
}

export interface UpdateOrganizationRequest {
  name?: string;
}

export interface UpdateOrganizationResponse {
  message: string;
  organization: {
    id: number;
    name: string;
  };
}

/**
 * Update organization details (name)
 */
export const updateOrganization = async (
  organizationId: number,
  data: UpdateOrganizationRequest
): Promise<UpdateOrganizationResponse> => {
  const response = await api.put(`/organizations/${organizationId}`, data);
  return response;
};

/**
 * Get organization branding settings (brandColor for booking pages)
 */
export const getBranding = async (organizationId: number): Promise<BrandingResponse> => {
  const response = await api.get(`/organizations/${organizationId}/branding`);
  return response;
};

/**
 * Update organization branding settings (brandColor for booking pages)
 */
export const updateBranding = async (
  organizationId: number,
  data: UpdateBrandingRequest
): Promise<{ message: string; branding: BrandingSettings }> => {
  const response = await api.put(`/organizations/${organizationId}/branding`, data);
  return response;
};

/**
 * Upload organization logo
 */
export const uploadLogo = async (
  organizationId: number,
  file: File
): Promise<{ message: string; imageUrl: string }> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/organizations/${organizationId}/logo`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to upload logo');
  }

  return response.json();
};

/**
 * Delete organization logo
 */
export const deleteLogo = async (organizationId: number): Promise<{ message: string }> => {
  const response = await api.delete(`/organizations/${organizationId}/logo`);
  return response;
};

/**
 * Get organization email branding settings
 */
export const getEmailBranding = async (organizationId: number): Promise<EmailBrandingResponse> => {
  const response = await api.get(`/organizations/${organizationId}/email-branding`);
  return response;
};

/**
 * Update organization email branding settings (header/footer HTML)
 */
export const updateEmailBranding = async (
  organizationId: number,
  data: UpdateEmailBrandingRequest
): Promise<{ message: string; emailBranding: EmailBrandingSettings }> => {
  const response = await api.put(`/organizations/${organizationId}/email-branding`, data);
  return response;
};

/**
 * Upload organization email logo
 */
export const uploadEmailLogo = async (
  organizationId: number,
  file: File
): Promise<{ message: string; imageUrl: string }> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/organizations/${organizationId}/email-branding/logo`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to upload email logo');
  }

  return response.json();
};

/**
 * Delete organization email logo
 */
export const deleteEmailLogo = async (organizationId: number): Promise<{ message: string }> => {
  const response = await api.delete(`/organizations/${organizationId}/email-branding/logo`);
  return response;
};

// ============================================
// Organization Switcher API Methods
// ============================================

/**
 * Get all organizations the current user belongs to
 * Used for organization switcher feature
 */
export const getUserOrganizations = async (): Promise<UserOrganizationsResponse> => {
  const response = await api.get('/users/me/organizations');
  return response;
};

/**
 * Switch to a different organization
 * Updates the user's current/primary organization
 */
export const switchOrganization = async (organizationId: number): Promise<SwitchOrganizationResponse> => {
  const response = await api.put('/users/me/organizations/current', { organizationId });
  return response;
};

const organizationService = {
  updateOrganization,
  getBranding,
  updateBranding,
  uploadLogo,
  deleteLogo,
  getEmailBranding,
  updateEmailBranding,
  uploadEmailLogo,
  deleteEmailLogo,
  getUserOrganizations,
  switchOrganization,
};

export default organizationService;
